import type { CurrencyCode } from './types';

/**
 * Shop configuration. Replaces the `Shop` constant, which had three fields and
 * no way to change any of them.
 *
 * Deliberately *not* an event: this is configuration, not a transaction, and
 * putting a shop-name edit through the "we could not save it, write it on paper"
 * durability path would be nonsense. The one part with audit value — the
 * exchange rate — is snapshotted onto each `Sale` instead, so an old bill stays
 * reconcilable after the rate moves.
 *
 * Mirrored by `ShopSettings` in `services/bof/apps/shops/models.py`, minus
 * `sellLayout` (a display preference that belongs to whoever is looking at the
 * screen and never leaves the client).
 */

/** LAK per one unit of the foreign currency. LAK itself is always 1. */
export type ShopRates = Record<Exclude<CurrencyCode, 'LAK'>, number>;

export const DEFAULT_RATES: ShopRates = { THB: 600, USD: 21500 };

export interface ShopSettings {
  name: string;
  /** Shown in the Home avatar. Derived from the name when left blank. */
  ownerInitial: string;
  phone?: string;
  address?: string;
  /** Printed under the invoice total. */
  invoiceFooter?: string;
  /**
   * Where a customer transfers the money. Printed on the bill itself, under the
   * total, so it travels with the piece of paper the customer walked out with.
   */
  bankNote?: string;
  /**
   * Which version of the shop's bank QR the server holds. 0 means none.
   *
   * Read-only: the server bumps it when the image is replaced. The image
   * itself is deliberately not here — it is fetched once from
   * `/api/v1/settings/qr` when this number moves, so 40KB of base64 does not
   * ride on every state answer and every five-second poll.
   */
  qrRev?: number;
  /**
   * How old a debt has to be before the shop wants to be told about it.
   * `chaseToday` prefers a debt that has reached it for the ຄວນທວງມື້ນີ້ card.
   */
  reminderDays: number;
  rates: ShopRates;
  /** Applied to a new product when the owner does not set its own threshold. */
  lowStockDefault: number;
}

export const DEFAULT_SHOP_SETTINGS: ShopSettings = {
  name: 'ຮ້ານ ວັດສະດຸ ພອນໄຊ',
  ownerInitial: 'ພ',
  reminderDays: 15,
  rates: DEFAULT_RATES,
  lowStockDefault: 20,
};

/** First character of the shop name, when the owner has not chosen one. */
export function initialOf(settings: ShopSettings): string {
  return settings.ownerInitial.trim() || settings.name.trim().charAt(0) || 'K';
}
